/**
 * Modular Favorites/Wishlist System for Multi-Tenant Discovery AI
 * Powered by LocalStorage with zero external dependencies.
 * Full Discovery AI GCS Metafield Image & Derivative Support.
 */

const STORAGE_KEY = 'theme_favorites_v1';

/**
 * Normalizes and formats Discovery AI GCS / CDN image URLs.
 * Supports derivatives: 'card' (/card/file.webp), 'thumb' (/thumb/file.webp), 'original' (full-res).
 * Ensures product images strictly use the GCS /card/ format (e.g. storage.googleapis.com/.../card/*.webp).
 */
function resolveFavoriteImage(imageUrl, format = 'card') {
  if (!imageUrl || typeof imageUrl !== 'string') return '';
  let url = imageUrl.trim();
  if (url.startsWith('//')) {
    url = 'https:' + url;
  }

  // 1. If already a Discovery AI GCS URL
  if (url.includes('discovery-ai-v3-public') || url.includes('storage.googleapis.com')) {
    // Strip query parameters
    const cleanUrl = url.split('?')[0];

    // Normalize any existing derivative folder out (/card/, /thumb/, /medium/, /full/)
    let normalized = cleanUrl
      .replace(/\/card\//g, '/')
      .replace(/\/thumb\//g, '/')
      .replace(/\/medium\//g, '/')
      .replace(/\/full\//g, '/');

    if (format === 'original' || format === 'full') {
      return normalized;
    }

    const lastSlash = normalized.lastIndexOf('/');
    if (lastSlash > -1) {
      const base = normalized.substring(0, lastSlash);
      let filename = normalized.substring(lastSlash + 1);
      // Ensure .webp extension for GCS derivative images
      if (!filename.endsWith('.webp')) {
        filename = filename.replace(/\.[^/.]+$/, '') + '.webp';
      }
      return `${base}/${format}/${filename}`;
    }
    return normalized;
  }

  return url;
}

class FavoritesManager {
  static getFavorites() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  static isFavorited(handle) {
    if (!handle) return false;
    return this.getFavorites().some(item => item.handle === handle);
  }

  static toggleFavorite(productData) {
    if (!productData || !productData.handle) return false;
    let favorites = this.getFavorites();
    const index = favorites.findIndex(item => item.handle === productData.handle);
    let isAdded = false;

    const defaultVendor = (document.documentElement.getAttribute('data-tenant') || 'Area Rug').toUpperCase();
    const resolvedCardImg = resolveFavoriteImage(productData.image || '', 'card');

    if (index > -1) {
      favorites.splice(index, 1);
    } else {
      favorites.unshift({
        handle: productData.handle,
        title: productData.title || 'Area Rug',
        image: resolvedCardImg || productData.image || '',
        price: productData.price || '',
        url: productData.url || `/products/${productData.handle}`,
        vendor: productData.vendor || defaultVendor,
        addedAt: Date.now()
      });
      isAdded = true;
    }

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
    } catch (e) {
      console.warn('Favorites: LocalStorage write failed', e);
    }

    window.dispatchEvent(new CustomEvent('favorites:updated', { detail: { favorites, isAdded, handle: productData.handle } }));
    return isAdded;
  }

  static updateFavoriteImage(handle, newImageUrl) {
    if (!handle || !newImageUrl) return;
    const cardUrl = resolveFavoriteImage(newImageUrl, 'card') || newImageUrl;
    let favorites = this.getFavorites();
    const item = favorites.find(f => f.handle === handle);
    if (item && item.image !== cardUrl) {
      item.image = cardUrl;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
      } catch (e) {}
      window.dispatchEvent(new CustomEvent('favorites:updated', { detail: { favorites, isAdded: false, handle } }));
    }
  }

  static removeFavorite(handle) {
    if (!handle) return;
    let favorites = this.getFavorites().filter(item => item.handle !== handle);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
    } catch (e) {}
    window.dispatchEvent(new CustomEvent('favorites:updated', { detail: { favorites, isAdded: false, handle } }));
  }

  static clearFavorites() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
    window.dispatchEvent(new CustomEvent('favorites:updated', { detail: { favorites: [] } }));
  }
}

/**
 * <favorites-button product-handle="x" product-title="y" product-image="z" product-price="10.00"></favorites-button>
 */
class FavoritesButton extends HTMLElement {
  connectedCallback() {
    const defaultVendor = (document.documentElement.getAttribute('data-tenant') || 'Area Rug').toUpperCase();
    this.handle = this.getAttribute('product-handle');
    this.title = this.getAttribute('product-title');
    this.image = this.getAttribute('product-image');
    this.price = this.getAttribute('product-price');
    this.url = this.getAttribute('product-url') || `/products/${this.handle}`;
    this.vendor = this.getAttribute('product-vendor') || defaultVendor;

    // Detect live GCS image from surrounding DOM if image is legacy CDN or missing
    if (!this.image || (!this.image.includes('discovery-ai-v3-public') && !this.image.includes('storage.googleapis.com'))) {
      const parentCard = this.closest('.card, .product-card-wrapper, .fav-card, .product, .product-grid, .ai-product-card');
      if (parentCard) {
        const gcsImg = parentCard.querySelector('img[src*="storage.googleapis.com"], img[src*="discovery-ai-v3-public"], [data-dai-src], [data-dai-main-image]');
        if (gcsImg) {
          const found = gcsImg.getAttribute('data-dai-src') || gcsImg.getAttribute('data-dai-main-image') || gcsImg.src;
          if (found) {
            this.image = resolveFavoriteImage(found, 'card');
            this.setAttribute('product-image', this.image);
          }
        }
      }
    }

    this.render();
    this.addEventListener('click', this.onClick.bind(this));

    this.updateHandler = () => this.updateState();
    window.addEventListener('favorites:updated', this.updateHandler);
  }

  disconnectedCallback() {
    window.removeEventListener('favorites:updated', this.updateHandler);
  }

  onClick(e) {
    e.preventDefault();
    e.stopPropagation();

    if (!this.handle) return;

    // Refresh live image if parent card or page now has a GCS image
    if (!this.image || (!this.image.includes('discovery-ai-v3-public') && !this.image.includes('storage.googleapis.com'))) {
      const parentCard = this.closest('.card, .product-card-wrapper, .fav-card, .product, .product-grid, .ai-product-card');
      if (parentCard) {
        const gcsImg = parentCard.querySelector('img[src*="storage.googleapis.com"], img[src*="discovery-ai-v3-public"], [data-dai-src], [data-dai-main-image]');
        if (gcsImg) {
          const found = gcsImg.getAttribute('data-dai-src') || gcsImg.getAttribute('data-dai-main-image') || gcsImg.src;
          if (found) this.image = found;
        }
      }
      if (!this.image || (!this.image.includes('discovery-ai-v3-public') && !this.image.includes('storage.googleapis.com'))) {
        const mainGcs = document.querySelector('.dai-product-gallery__main-image, [data-dai-main-image], [data-dai-src]');
        if (mainGcs) {
          const found = mainGcs.getAttribute('data-dai-src') || mainGcs.getAttribute('data-dai-main-image') || mainGcs.src;
          if (found) this.image = found;
        }
      }
    }

    const resolvedCardImg = resolveFavoriteImage(this.image || '', 'card');

    FavoritesManager.toggleFavorite({
      handle: this.handle,
      title: this.title,
      image: resolvedCardImg || this.image,
      price: this.price,
      url: this.url,
      vendor: this.vendor
    });

    const btn = this.querySelector('button');
    if (btn) {
      btn.style.transform = 'scale(1.2)';
      setTimeout(() => { btn.style.transform = 'scale(1)'; }, 180);
    }
  }

  updateState() {
    const isFavorited = FavoritesManager.isFavorited(this.handle);
    const svg = this.querySelector('svg');
    const btn = this.querySelector('button');
    if (svg) {
      if (isFavorited) {
        svg.setAttribute('fill', '#d75437');
        svg.style.color = '#d75437';
      } else {
        svg.setAttribute('fill', 'none');
        svg.style.color = 'currentColor';
      }
    }
    if (btn) {
      btn.setAttribute('aria-label', isFavorited ? 'Remove from Wishlist' : 'Add to Wishlist');
      btn.setAttribute('title', isFavorited ? 'Remove from Wishlist' : 'Add to Wishlist');
    }
  }

  render() {
    const isFavorited = FavoritesManager.isFavorited(this.handle);
    const fill = isFavorited ? '#d75437' : 'none';
    const color = isFavorited ? '#d75437' : 'currentColor';

    this.innerHTML = `
      <button type="button" class="favorites-btn" aria-label="${isFavorited ? 'Remove from Wishlist' : 'Add to Wishlist'}" title="${isFavorited ? 'Remove from Wishlist' : 'Add to Wishlist'}" style="background: none; border: none; cursor: pointer; padding: 6px; display: inline-flex; align-items: center; justify-content: center; transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="${fill}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: ${color}; transition: all 0.2s ease;">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
        </svg>
      </button>
    `;
  }
}
if (!customElements.get('favorites-button')) {
  customElements.define('favorites-button', FavoritesButton);
}

/**
 * <favorites-counter></favorites-counter>
 */
class FavoritesCounter extends HTMLElement {
  connectedCallback() {
    this.render();
    this.updateHandler = () => this.render();
    window.addEventListener('favorites:updated', this.updateHandler);
  }

  disconnectedCallback() {
    window.removeEventListener('favorites:updated', this.updateHandler);
  }

  render() {
    const count = FavoritesManager.getFavorites().length;
    if (count > 0) {
      this.innerHTML = `
        <a href="/pages/favorites" class="header__icon link focus-inset" style="position: relative;" aria-label="Favorites (${count} items)">
          <svg class="icon icon-heart" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
          <div class="cart-count-bubble" style="animation: fav-badge-pop 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);">
            <span aria-hidden="true">${count}</span>
            <span class="visually-hidden">${count} item(s) in favorites</span>
          </div>
        </a>
      `;
    } else {
      this.innerHTML = `
        <a href="/pages/favorites" class="header__icon link focus-inset" aria-label="Favorites (0 items)">
          <svg class="icon icon-heart" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
        </a>
      `;
    }
  }
}
if (!customElements.get('favorites-counter')) {
  customElements.define('favorites-counter', FavoritesCounter);
}

/**
 * <favorites-grid></favorites-grid>
 */
class FavoritesGrid extends HTMLElement {
  connectedCallback() {
    this.render();
    this.updateHandler = () => this.render();
    window.addEventListener('favorites:updated', this.updateHandler);

    this.bindHeaderActions();
  }

  disconnectedCallback() {
    window.removeEventListener('favorites:updated', this.updateHandler);
  }

  bindHeaderActions() {
    const clearBtn = document.getElementById('fav-clear-btn');
    if (clearBtn && !clearBtn._bound) {
      clearBtn._bound = true;
      clearBtn.addEventListener('click', () => {
        if (confirm('Clear all saved favorites?')) {
          FavoritesManager.clearFavorites();
        }
      });
    }
  }

  render() {
    const favorites = FavoritesManager.getFavorites();
    const countDisplay = document.getElementById('fav-count-display');
    const clearBtn = document.getElementById('fav-clear-btn');

    if (countDisplay) {
      countDisplay.textContent = favorites.length > 0 ? `(${favorites.length})` : '';
    }
    if (clearBtn) {
      clearBtn.style.display = favorites.length > 0 ? 'inline-block' : 'none';
    }

    if (favorites.length === 0) {
      this.innerHTML = `
        <div class="fav-empty-state" style="text-align: center; padding: 4rem 1rem;">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" style="opacity: 0.4; margin-bottom: 1rem;"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
          <h2 class="h3" style="margin-bottom: 0.5rem;">Your wishlist is empty</h2>
          <p style="opacity: 0.7; margin-bottom: 1.5rem; max-width: 420px; margin-left: auto; margin-right: auto; line-height: 1.5;">Browse our products and click the heart icon to save your favorite rugs for later.</p>
          <a href="/collections/all" class="button button--primary">Continue Shopping</a>
        </div>
      `;
      return;
    }

    const defaultVendor = (document.documentElement.getAttribute('data-tenant') || 'Area Rug').toUpperCase();

    const cardsHtml = favorites.map(item => {
      const handle = item.handle;
      const title = item.title || 'Area Rug';
      const rawImage = item.image || '';
      const isGcs = rawImage && (rawImage.includes('discovery-ai-v3-public') || rawImage.includes('storage.googleapis.com'));
      
      const cardImageUrl = isGcs ? resolveFavoriteImage(rawImage, 'card') : rawImage;
      const highResRugUrl = isGcs ? resolveFavoriteImage(rawImage, 'original') : rawImage;
      const price = item.price || '';
      const url = item.url || `/products/${handle}`;
      const vendor = item.vendor || defaultVendor;

      // Auto-upgrade legacy CDN or missing images to Discovery AI GCS images
      if (handle && !isGcs) {
        fetch(`/products/${handle}`)
          .then(r => r.text())
          .then(html => {
            let foundGcsUrl = '';
            // 1. Look for GCS image in favorites-button attribute
            const favBtnMatch = html.match(/<favorites-button[^>]+product-image="([^"]+)"/i);
            if (favBtnMatch && (favBtnMatch[1].includes('discovery-ai-v3-public') || favBtnMatch[1].includes('storage.googleapis.com'))) {
              foundGcsUrl = favBtnMatch[1];
            }
            // 2. Look for primary_image_url in JSON script / metafields
            if (!foundGcsUrl) {
              const primaryMatch = html.match(/"primary_image_url"\s*:\s*"([^"]+)"/i);
              if (primaryMatch && (primaryMatch[1].includes('discovery-ai-v3-public') || primaryMatch[1].includes('storage.googleapis.com'))) {
                foundGcsUrl = primaryMatch[1].replace(/\\/g, '');
              }
            }
            // 3. Look for any GCS URL in the HTML
            if (!foundGcsUrl) {
              const gcsMatch = html.match(/https?:\/\/(?:storage\.googleapis\.com\/discovery-ai-v3-public|discovery-ai-v3-public)[^"'\s<>\\]+\.webp/i);
              if (gcsMatch) {
                foundGcsUrl = gcsMatch[0];
              }
            }
            if (foundGcsUrl) {
              const resolvedCard = resolveFavoriteImage(foundGcsUrl, 'card');
              const resolvedOrig = resolveFavoriteImage(foundGcsUrl, 'original');
              FavoritesManager.updateFavoriteImage(handle, resolvedCard);
              
              const cardEl = this.querySelector(`.fav-card[data-handle="${handle}"]`);
              if (cardEl) {
                const imgEl = cardEl.querySelector('.fav-image-wrap img');
                if (imgEl) {
                  imgEl.src = resolvedCard;
                  imgEl.dataset.fallback = resolvedOrig;
                }
                const roomBtn = cardEl.querySelector('.see-in-room-overlay-btn');
                if (roomBtn) {
                  roomBtn.setAttribute('data-rug', resolvedOrig || resolvedCard);
                }
              }
            }
          })
          .catch(() => {});
      }

      return `
        <div class="fav-card" data-handle="${handle}" style="display: flex; flex-direction: column; height: 100%; position: relative;">
          
          <!-- Image block with overlays -->
          <div class="fav-image-wrap" style="position: relative; width: 100%; aspect-ratio: 4 / 5.2; background: rgba(var(--fav-fg, 26, 26, 26), 0.04); border-radius: 6px; overflow: hidden;">
            <a href="${url}" class="fav-product-link" style="display: block; width: 100%; height: 100%;">
              <img
                src="${cardImageUrl || rawImage}"
                data-fallback="${highResRugUrl || rawImage}"
                alt="${title.replace(/"/g, '&quot;')}"
                loading="lazy"
                style="width: 100%; height: 100%; object-fit: cover; transition: transform 0.3s ease;"
                onerror="if(this.dataset.fallback && this.src !== this.dataset.fallback){const fb=this.dataset.fallback; delete this.dataset.fallback; this.src=fb;}"
              />
            </a>

            <!-- Overlay Heart / Remove button -->
            <button type="button" class="fav-remove-btn" data-handle="${handle}" aria-label="Remove from favorites" title="Remove from favorites" style="position: absolute; top: 8px; right: 8px; z-index: 3; background: rgba(var(--fav-bg, 255, 255, 255), 0.92); border: none; border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; cursor: pointer; box-shadow: 0 2px 5px rgba(0,0,0,0.15); transition: transform 0.2s ease;">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#d75437" stroke="#d75437" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              </svg>
            </button>

            <!-- See in My Room Overlay button -->
            <button type="button" class="see-in-room-overlay-btn visualizer-trigger" data-rug="${highResRugUrl || cardImageUrl || rawImage}" data-product-url="${url}" data-product-handle="${handle}" style="position: absolute; bottom: 8px; left: 8px; background: rgba(var(--fav-bg, 255, 255, 255), 0.88); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); border: 1px solid rgba(var(--fav-fg, 26, 26, 26), 0.12); color: rgb(var(--fav-fg, 26, 26, 26)); padding: 5px 10px; font-size: 10.5px; font-weight: 600; border-radius: 16px; display: flex; align-items: center; justify-content: center; gap: 5px; box-shadow: 0 2px 6px rgba(0,0,0,0.18); cursor: pointer; z-index: 3; text-decoration: none; transition: all 0.2s ease;">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
              See in My Room
            </button>
          </div>

          <!-- Card Content Details -->
          <div style="padding-top: 10px; display: flex; flex-direction: column; flex-grow: 1;">
            <div style="font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; color: rgb(var(--fav-fg, 26, 26, 26)); opacity: 0.72; margin-bottom: 2px; font-weight: 500;">
              ${vendor}
            </div>
            
            <a href="${url}" style="text-decoration: none; color: inherit; display: block;">
              <p style="font-size: 13px; font-weight: 400; line-height: 1.35; min-height: 35px; max-height: 35px; color: rgb(var(--fav-fg, 26, 26, 26)); margin: 0; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; text-overflow: ellipsis;">
                ${title}
              </p>
            </a>

            <!-- Price row -->
            <div style="margin-top: auto; padding-top: 6px; display: flex; align-items: center; gap: 6px; min-height: 22px;">
              <span style="color: #d75437; font-weight: 700; font-size: 14.5px; line-height: 1.2;">
                ${price || 'View Details'}
              </span>
            </div>

            <!-- Direct AJAX Add to Cart Button -->
            <div style="margin-top: 8px; width: 100%;">
              <button type="button" class="fav-add-cart-btn ai-add-to-cart-btn" data-handle="${handle}" aria-label="Add to cart">
                <svg class="ai-cart-icon" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 20 20" fill="none" style="stroke: #ffffff; min-width: 14px; min-height: 14px; display: inline-block;">
                  <path stroke="#ffffff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none" d="M3.392 6.875h13.216v8.016c0 .567-.224 1.112-.624 1.513-.4.402-.941.627-1.506.627H5.522a2.13 2.13 0 0 1-1.506-.627 2.15 2.15 0 0 1-.624-1.513zM8.818 2.969h2.333c.618 0 1.211.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.313c0-.622.246-1.218.683-1.658a2.33 2.33 0 0 1 1.65-.686"/>
                </svg>
                <span class="ai-add-to-cart-text">Add to cart</span>
              </button>
            </div>

          </div>
        </div>
      `;
    }).join('');

    this.innerHTML = `
      <div class="fav-grid" style="display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 16px;">
        ${cardsHtml}
      </div>
    `;

    this.bindCardEvents();
  }

  bindCardEvents() {
    // 1. Remove Item with smooth exit animation
    this.querySelectorAll('.fav-remove-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const handle = btn.getAttribute('data-handle');
        const card = btn.closest('.fav-card');

        if (card) {
          card.style.opacity = '0';
          card.style.transform = 'scale(0.9)';
          card.style.transition = 'all 0.2s ease';
          setTimeout(() => {
            FavoritesManager.removeFavorite(handle);
          }, 200);
        } else {
          FavoritesManager.removeFavorite(handle);
        }
      });
    });

    // 2. Direct AJAX Add to Cart & Open Cart Drawer
    this.querySelectorAll('.fav-add-cart-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const handle = btn.getAttribute('data-handle');
        const textSpan = btn.querySelector('.ai-add-to-cart-text');
        const originalText = textSpan ? textSpan.textContent : 'Add to cart';

        if (textSpan) textSpan.textContent = 'Adding...';
        btn.disabled = true;

        try {
          // Resolve numeric variant ID
          const res = await fetch(`/products/${handle}.js`);
          const prodData = await res.json();
          const firstVariant = prodData && prodData.variants && prodData.variants.length > 0 ? prodData.variants[0] : null;

          if (!firstVariant || !firstVariant.id) {
            throw new Error('Variant unavailable');
          }

          const addRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{ id: Number(firstVariant.id), quantity: 1 }],
              sections: 'cart-drawer,cart-icon-bubble,cart-notification'
            })
          });

          if (addRes.ok) {
            const cartData = await addRes.json();
            if (textSpan) textSpan.textContent = 'Added! ✓';
            btn.style.setProperty('background', '#2e7d32', 'important');

            // Dispatch global cart updates
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cartData } }));
            document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cartData }));

            // Open native cart drawer
            const cartDrawer = document.querySelector('cart-drawer, #CartDrawer, #cart-drawer');
            if (cartDrawer) {
              if (cartData.sections && cartData.sections['cart-drawer'] && typeof cartDrawer.renderContents === 'function') {
                try { cartDrawer.renderContents(cartData); } catch (_) {}
              }
              if (typeof cartDrawer.open === 'function') {
                cartDrawer.open();
              } else if (cartDrawer.classList.contains('drawer') || cartDrawer.id === 'CartDrawer') {
                cartDrawer.classList.add('animate', 'active');
                document.body.classList.add('overflow-hidden');
              }
            }

            setTimeout(() => {
              if (textSpan) textSpan.textContent = originalText;
              btn.style.removeProperty('background');
              btn.disabled = false;
            }, 2000);
          } else {
            throw new Error('Add to cart failed');
          }
        } catch (err) {
          console.warn('Favorites add to cart error:', err);
          if (textSpan) textSpan.textContent = 'Unavailable';
          btn.style.setProperty('background', '#777', 'important');
          setTimeout(() => {
            if (textSpan) textSpan.textContent = originalText;
            btn.style.removeProperty('background');
            btn.disabled = false;
          }, 2000);
        }
      });
    });
  }
}
if (!customElements.get('favorites-grid')) {
  customElements.define('favorites-grid', FavoritesGrid);
}

// Global CSS injection for favorites animations
(function() {
  if (document.getElementById('favorites-core-styles')) return;
  const style = document.createElement('style');
  style.id = 'favorites-core-styles';
  style.textContent = `
    @keyframes fav-badge-pop {
      0% { transform: scale(0.6); opacity: 0; }
      70% { transform: scale(1.2); }
      100% { transform: scale(1); opacity: 1; }
    }
    .fav-image-wrap:hover img {
      transform: scale(1.03);
    }
  `;
  document.head.appendChild(style);
})();
